/* eslint-disable react/prop-types */
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  IconButton,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Close as CloseIcon,
  ViewList as ViewListIcon,
  Assignment as SectionIcon,
  HelpOutline as QuestionIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

const ChecklistDetails = ({ checklist, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!checklist?.items) return { sections: 0, questions: 0, total: 0 };

    const sections = checklist.items.filter((item) => item.is_section).length;
    const questions = checklist.items.filter((item) => !item.is_section).length;
    const total = checklist.items.length;

    return { sections, questions, total };
  }, [checklist?.items]);

  if (!checklist) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          bgcolor: alpha(theme.palette.error.main, 0.05),
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
        }}
      >
        <Typography color="error" variant="h6" sx={{ fontWeight: 600 }}>
          {t("checklistDetails.errorText")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          py: 3,
          px: 4,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
        component="div"
        id="checklist-details-dialog-title"
      >
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            {checklist.name || t("checklistDetails.unknownName")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Chip
              icon={<ViewListIcon />}
              label={t("checklistDetails.totalItemsChip", {
                count: statistics.total,
              })}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                fontWeight: 600,
                "& .MuiChip-icon": {
                  color: theme.palette.primary.main,
                },
              }}
              variant="outlined"
            />
            {statistics.sections > 0 && (
              <Chip
                icon={<SectionIcon />}
                label={t("checklistDetails.sectionsChip", {
                  count: statistics.sections,
                })}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  fontWeight: 600,
                  "& .MuiChip-icon": {
                    color: theme.palette.secondary.main,
                  },
                }}
                variant="outlined"
              />
            )}
            {statistics.questions > 0 && (
              <Chip
                icon={<QuestionIcon />}
                label={t("checklistDetails.questionsChip", {
                  count: statistics.questions,
                })}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  fontWeight: 600,
                  "& .MuiChip-icon": {
                    color: theme.palette.info.main,
                  },
                }}
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label={t("checklistDetails.closeDialogAria")}
          sx={{
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            "&:hover": {
              bgcolor: alpha(theme.palette.text.primary, 0.12),
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease",
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          bgcolor: "background.default",
          minHeight: 400,
        }}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          {/* Overview Section */}
          <Paper
            elevation={0}
            sx={{
              m: 3,
              mb: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              background: alpha(theme.palette.primary.main, 0.02),
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: alpha(theme.palette.primary.main, 0.3),
                boxShadow: theme.shadows[4],
              },
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 2,
                }}
              >
                <DescriptionIcon
                  sx={{
                    mt: 0.5,
                    color: theme.palette.primary.main,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {t("checklistDetails.overviewTitle")}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {checklist.description ||
                      t("checklistDetails.unknownDescription")}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarIcon
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "small",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      <strong>{t("checklistDetails.createdAtLabel")}:</strong>{" "}
                      {convertUTCToLocal(checklist.created_at) ||
                        t("checklistDetails.unknownDate")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ViewListIcon
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "small",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      <strong>{t("checklistDetails.structureLabel")}:</strong>{" "}
                      {statistics.sections > 0
                        ? t(
                            "checklistDetails.structureWithSections",
                            statistics
                          )
                        : t("checklistDetails.structureQuestionsOnly", {
                            questions: statistics.questions,
                          })}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Paper>

          {/* Items Section */}
          <Box sx={{ px: 3, pb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t("checklistDetails.itemsTitle")}
            </Typography>

            {checklist.items.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: "center",
                  bgcolor: alpha(theme.palette.grey[500], 0.05),
                  border: `2px dashed ${alpha(theme.palette.grey[500], 0.3)}`,
                  borderRadius: 3,
                }}
              >
                <ViewListIcon
                  sx={{
                    fontSize: 64,
                    color: "text.disabled",
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {t("checklistDetails.noItemsTitle")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.disabled",
                  }}
                >
                  {t("checklistDetails.noItemsDescription")}
                </Typography>
              </Paper>
            ) : (
              checklist.items.map((item, index) => (
                <Paper
                  key={item.id}
                  elevation={0}
                  sx={{
                    mb: 2,
                    border: "1px solid",
                    borderColor: item.is_section
                      ? alpha(theme.palette.secondary.main, 0.2)
                      : alpha(theme.palette.info.main, 0.2),
                    borderRadius: 3,
                    bgcolor: item.is_section
                      ? alpha(theme.palette.secondary.main, 0.03)
                      : alpha(theme.palette.info.main, 0.03),
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: theme.shadows[8],
                      borderColor: item.is_section
                        ? alpha(theme.palette.secondary.main, 0.4)
                        : alpha(theme.palette.info.main, 0.4),
                    },
                    position: "relative",
                    overflow: "visible",
                  }}
                  aria-label={t("checklistDetails.itemAria", {
                    type: item.is_section
                      ? t("checklistDetails.sectionType")
                      : t("checklistDetails.questionType"),
                    text: item.text || t("checklistDetails.unknownItemText"),
                  })}
                >
                  {/* Item type badge */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -8,
                      left: 16,
                      zIndex: 1,
                    }}
                  >
                    <Chip
                      icon={
                        item.is_section ? <SectionIcon /> : <QuestionIcon />
                      }
                      label={
                        item.is_section
                          ? t("checklistDetails.sectionBadge")
                          : t("checklistDetails.questionBadge")
                      }
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: item.is_section
                          ? theme.palette.secondary.main
                          : theme.palette.info.main,
                        color: theme.palette.common.white,
                        boxShadow: theme.shadows[2],
                        "& .MuiChip-icon": {
                          fontSize: "0.8rem",
                          color: theme.palette.common.white,
                        },
                      }}
                    />
                  </Box>

                  <CardContent sx={{ pt: 3 }}>
                    {item.is_section ? (
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "text.primary",
                            mb: 1,
                          }}
                        >
                          {item.text || t("checklistDetails.unknownItemText")}
                        </Typography>
                        {item.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontStyle: "italic",
                              pl: 2,
                              borderLeft: `3px solid ${theme.palette.secondary.main}`,
                              bgcolor: alpha(
                                theme.palette.secondary.main,
                                0.05
                              ),
                              borderRadius: 1,
                              py: 1,
                            }}
                          >
                            {item.description}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            mb: item.description ? 1 : 0,
                            color: "text.primary",
                          }}
                        >
                          {item.text || t("checklistDetails.unknownItemText")}
                        </Typography>
                        {item.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              pl: 2,
                              borderLeft: `3px solid ${theme.palette.info.main}`,
                              bgcolor: alpha(theme.palette.info.main, 0.05),
                              borderRadius: 1,
                              py: 1,
                            }}
                          >
                            {item.description}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Item number indicator */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 12,
                        right: 12,
                        bgcolor: alpha(theme.palette.grey[500], 0.1),
                        color: "text.secondary",
                        borderRadius: "50%",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: theme.shadows[1],
                      }}
                    >
                      {index + 1}
                    </Box>
                  </CardContent>
                </Paper>
              ))
            )}
          </Box>
        </Box>
      </DialogContent>
    </>
  );
};

export default ChecklistDetails;
